/*
 * Copyright 2026 Sony Corporation
 */

import BlurOnIcon from '@mui/icons-material/BlurOn';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Asset, AssetKind } from '@ips-device-verse/twin-service/schema/assets';

import { ACCEPTED_EXTENSIONS, refusalOf, uploadAsset } from '../../services/asset-upload';
import { anyRunning, failedOf, isRunning, statusLine, viewOf } from '../../services/library-view';
import { useAuth } from '../../contexts/auth-context';
import type { SceneOp } from '../../scene-doc/schema';

const KIND_ICONS: Record<AssetKind, typeof ViewInArIcon> = {
  model: ViewInArIcon,
  splat: BlurOnIcon,
  video: VideocamIcon,
};

interface Upload {
  key: string;
  name: string;
  progress: number;
}

const chipOf = (asset: Asset): string => {
  if (asset.status === 'ready') {
    return asset.kind;
  }
  if (asset.status === 'failed') {
    return 'failed';
  }
  return isRunning(asset) ? 'working' : 'unfinished';
};

interface TileProps {
  asset: Asset;
  onPlace: (asset: Asset) => void;
  onRemove: (asset: Asset) => void;
  /** Laid over the tile's lower right, for what only this kind of tile can do. */
  action?: ReactNode;
}

/** Square by percentage padding: see AssetGrid. The buttons are siblings of the tile's own, so each stays usable. */
const Tile = ({ asset, onPlace, onRemove, action }: TileProps) => {
  const Icon = KIND_ICONS[asset.kind];
  const placeable = asset.status === 'ready' && asset.kind !== 'video';
  return (
    <Box className="group relative h-0 w-full overflow-hidden rounded pb-[100%]">
      <Tooltip title={asset.failure ?? asset.name} placement="top">
        <Box
          component="button"
          aria-label={asset.name}
          aria-disabled={!placeable}
          onClick={() => placeable && onPlace(asset)}
          className="absolute inset-0 block border-0 p-0"
          sx={{
            bgcolor: 'action.hover',
            cursor: placeable ? 'pointer' : 'default',
            opacity: asset.status === 'ready' ? 1 : 0.55,
            '&:hover': placeable ? { outline: '2px solid', outlineColor: 'primary.main' } : {},
          }}
        >
          {asset.thumbnail_url ? (
            <Box
              component="img"
              src={asset.thumbnail_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <Box className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-1">
              {isRunning(asset) ? <CircularProgress size={20} /> : <Icon color="action" />}
              <Typography variant="caption" className="w-full truncate text-center leading-tight">
                {asset.name}
              </Typography>
            </Box>
          )}
          <Chip
            label={chipOf(asset)}
            size="small"
            color={asset.status === 'failed' ? 'error' : 'default'}
            className="absolute bottom-1 left-1 h-4 text-[0.6rem]"
          />
        </Box>
      </Tooltip>
      {action && <Box className="absolute right-1 bottom-1">{action}</Box>}
      <IconButton
        size="small"
        aria-label={`${isRunning(asset) ? 'Cancel' : 'Delete'} ${asset.name}`}
        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
        onClick={() => onRemove(asset)}
      >
        {isRunning(asset) ? <CloseIcon fontSize="inherit" /> : <DeleteOutlineIcon fontSize="inherit" />}
      </IconButton>
    </Box>
  );
};

/** Long enough that a stage change shows within a glance, short against a job of tens of minutes (§8.2). */
const POLL_MS = 10_000;

/**
 * The user's own uploads and reconstructions (3d-reconstruction.md §9).
 *
 * A ready model or splat is placed at the origin, not a free spot: a
 * reconstruction is already where it belongs (§6.4), and that is what makes a
 * splat and the mesh from the same run overlay exactly.
 */
export const MyLibrary = ({ dispatch }: { dispatch: (ops: readonly SceneOp[]) => void }) => {
  const { api } = useAuth();
  const [assets, setAssets] = useState<Asset[]>();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [error, setError] = useState<string>();
  const [over, setOver] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const input = useRef<HTMLInputElement>(null);

  const refresh = useCallback(
    (signal?: AbortSignal) =>
      api.listAssets(signal).then(
        (listed) => {
          setAssets(listed);
          setNow(Date.now());
        },
        (cause: Error) => {
          if (!signal?.aborted) {
            setError(cause.message);
          }
        }
      ),
    [api]
  );

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  // Only while something is running, and only while anyone can see it.
  const running = assets !== undefined && anyRunning(assets);
  useEffect(() => {
    if (!running) {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, refresh]);

  const view = useMemo(() => viewOf(assets ?? []), [assets]);
  const reconstructing = new Set(
    view.reconstructions
      .filter((run) => [run.splat, run.mesh].some((output) => output && isRunning(output)))
      .map((run) => run.source?.asset_id)
  );

  const upload = async (files: readonly File[]) => {
    setError(undefined);
    for (const file of files) {
      const refusal = refusalOf(file);
      if (refusal) {
        setError(refusal);
        continue;
      }
      const key = `${file.name}:${Date.now()}`;
      setUploads((current) => [...current, { key, name: file.name, progress: 0 }]);
      try {
        await uploadAsset(api, file, (progress) =>
          setUploads((current) => current.map((item) => (item.key === key ? { ...item, progress } : item)))
        );
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : `${file.name} could not be uploaded.`);
      } finally {
        setUploads((current) => current.filter((item) => item.key !== key));
        await refresh();
      }
    }
  };

  const act = async (work: () => Promise<unknown>, failure: string) => {
    setError(undefined);
    try {
      await work();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : failure);
    }
    await refresh();
  };

  const remove = (asset: Asset) => {
    const question = isRunning(asset)
      ? `Cancel making ${asset.name}? What has been computed so far is discarded.`
      : `Delete ${asset.name}? Scenes already published keep their copy.`;
    if (window.confirm(question)) {
      void act(() => api.deleteAsset(asset.asset_id), 'The asset could not be deleted.');
    }
  };

  const reconstruct = (video: Asset) =>
    void act(() => api.reconstructAsset(video.asset_id), 'The reconstruction could not be started.');

  /** A run cannot restart one output, so Retry clears what failed and runs the video again. */
  const retry = (failed: readonly Asset[], video: Asset) =>
    void act(async () => {
      for (const asset of failed) {
        await api.deleteAsset(asset.asset_id);
      }
      await api.reconstructAsset(video.asset_id);
    }, 'The reconstruction could not be started.');

  const place = (asset: Asset) => {
    dispatch([{ op: 'add_asset', source: 'library', slug: asset.asset_id, name: asset.name, position: [0, 0, 0] }]);
  };

  return (
    <Box className="flex min-h-0 flex-1 flex-col">
      <Box
        className="m-3 flex flex-col items-center gap-1 rounded border border-dashed p-3 text-center"
        sx={{ borderColor: over ? 'primary.main' : 'divider', bgcolor: over ? 'action.hover' : undefined }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes('Files')) {
            event.preventDefault();
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          void upload([...event.dataTransfer.files]);
        }}
      >
        <CloudUploadIcon color="action" />
        <Typography variant="caption" color="text.secondary">
          Drop {ACCEPTED_EXTENSIONS.join(' ')} here
        </Typography>
        <Button size="small" onClick={() => input.current?.click()} sx={{ textTransform: 'none' }}>
          Choose files
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(event) => {
            void upload([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
        <Typography variant="caption" color="text.secondary" className="leading-tight">
          For a video to reconstruct: walk a slow loop at two heights, keep the subject in frame, lock exposure, avoid
          mirrors and moving people, 30–90 s.
        </Typography>
      </Box>

      {uploads.map((item) => (
        <Box key={item.key} className="px-3 pb-2">
          <Typography variant="caption" noWrap>
            Uploading {item.name}
          </Typography>
          <LinearProgress variant="determinate" value={item.progress * 100} />
        </Box>
      ))}

      {error && (
        <Typography variant="caption" color="error" className="px-3 pb-2">
          {error}
        </Typography>
      )}

      {assets === undefined ? (
        <LinearProgress className="mx-3" />
      ) : assets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" className="px-4">
          Nothing uploaded yet.
        </Typography>
      ) : (
        <Box className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
          {view.reconstructions.map((run) => {
            const outputs = [run.splat, run.mesh].filter((output): output is Asset => output !== undefined);
            const failed = failedOf(run);
            return (
              <Box
                key={run.reconstruction_id}
                className="mb-3 rounded border p-2"
                sx={{ borderColor: 'divider' }}
                aria-label={`Reconstruction of ${run.source?.name ?? 'a deleted video'}`}
              >
                <Box className="mb-1 flex items-center justify-between gap-1">
                  <Typography variant="caption" color="text.secondary" noWrap>
                    From {run.source?.name ?? 'a deleted video'}
                  </Typography>
                  {failed.length > 0 && run.source?.status === 'ready' && (
                    <Button
                      size="small"
                      onClick={() => run.source && retry(failed, run.source)}
                      sx={{ textTransform: 'none', minWidth: 0, py: 0 }}
                    >
                      Retry
                    </Button>
                  )}
                </Box>
                <Box className="grid grid-cols-3 gap-2">
                  {outputs.map((output) => (
                    <Tile key={output.asset_id} asset={output} onPlace={place} onRemove={remove} />
                  ))}
                </Box>
                {outputs.map((output) => {
                  const line = statusLine(output, now);
                  return (
                    line && (
                      <Typography
                        key={output.asset_id}
                        variant="caption"
                        component="p"
                        color={output.status === 'failed' ? 'error' : 'text.secondary'}
                        className="mt-1 leading-tight"
                      >
                        {output.kind === 'splat' ? 'Splat' : 'Mesh'}: {line}
                      </Typography>
                    )
                  );
                })}
              </Box>
            );
          })}

          <Box className="grid grid-cols-3 content-start gap-2">
            {view.others.map((asset) => (
              <Tile
                key={asset.asset_id}
                asset={asset}
                onPlace={place}
                onRemove={remove}
                action={
                  asset.kind === 'video' &&
                  asset.status === 'ready' && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={reconstructing.has(asset.asset_id)}
                      onClick={() => reconstruct(asset)}
                      sx={{ textTransform: 'none', fontSize: '0.6rem', minWidth: 0, px: 0.75, py: 0 }}
                    >
                      Reconstruct
                    </Button>
                  )
                }
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
